import { ChangeDetectionStrategy, Component, DestroyRef, computed, effect, inject, input, model, output, signal, untracked } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { TitleCasePipe } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Button } from 'primeng/button';
import { CheckboxModule } from 'primeng/checkbox';
import { DialogModule } from 'primeng/dialog';
import { SelectModule } from 'primeng/select';
import { MultiSelectModule } from 'primeng/multiselect';
import { TableModule } from 'primeng/table';
import { Tabs, TabList, Tab, TabPanels, TabPanel } from 'primeng/tabs';
import { DatePicker } from 'primeng/datepicker';
import { ToggleSwitch } from 'primeng/toggleswitch';
import { RadioButtonModule } from 'primeng/radiobutton';
import { InputTextModule } from 'primeng/inputtext';
import { MessageService } from 'primeng/api';
import { catchError } from 'rxjs/operators';
import { of } from 'rxjs';

import { RelatorioSelecaoPjService } from '@/core/api/sicom-api/registro/prestador/relatorios/relatorio-selecao-pj/relatorio-selecao-pj.service';
import { ETipoGestaoPrestador, PrestadorListItem, RelatorioSelecaoPjPesquisaFiltros } from '@/core/api/sicom-api/registro/prestador/relatorios/relatorio-selecao-pj/relatorio-selecao-pj.model';
import { PrestadorHistoricoService } from '@/core/api/sicom-api/registro/prestador/prestador-historico.service';
import { PrestadorHistoricoOpcao } from '@/core/api/sicom-api/registro/prestador/prestador-historico.model';
import { PrestadorRecursosHumanosService } from '@/core/api/sicom-api/registro/prestador/prestador-recursos-humanos.service';
import { PrestadorRecursosMateriaisService } from '@/core/api/sicom-api/registro/prestador/prestador-recursos-materiais.service';
import { PrestadorServicosEspecialidadesService } from '@/core/api/sicom-api/registro/prestador/prestador-servicos-especialidades.service';
import { PrestadorAtividadesService } from '@/core/api/sicom-api/registro/prestador/prestador-atividades.service';
import { EspecialidadeService } from '@/core/api/sicom-api/registro/medico/especialidade/especialidade.service';
import { AreaAtuacaoPorEspecialidadeResponse, EspecialidadeListaResponse } from '@/core/api/sicom-api/registro/medico/especialidade/especialidade.model';
import { MunicipioResponse } from '@/core/api/sicom-api/registro/medico/municipio/municipio.model';
import { DelegaciaResumoApiResponse } from '@/core/api/sicom-api/registro/orgao/delegacia.model';
import { PesquisaMunicipio } from './pesquisa-municipio/pesquisa-municipio';
import { PesquisaDelegacia } from './pesquisa-delegacia/pesquisa-delegacia';

import { DialogoPadraoDirective } from '@/shared/components/layout/dialogo-padrao/dialogo-padrao.directive';
const UF_OPTIONS = ['AC', 'AL', 'AM', 'AP', 'BA', 'CE', 'DF', 'ES', 'GO', 'MA', 'MG', 'MS', 'MT', 'PA', 'PB', 'PE', 'PI', 'PR', 'RJ', 'RN', 'RO', 'RR', 'RS', 'SC', 'SE', 'SP', 'TO'];

const UF_SELECT_OPTIONS = [{ label: 'Todas', value: '' }, ...UF_OPTIONS.map((uf) => ({ label: uf, value: uf }))];

const REG_CAD_OPTIONS = [
    { label: 'Todos', value: '' },
    { label: 'Cadastro', value: 'C' },
    { label: 'Registro', value: 'R' }
];

const SITUACAO_REGISTRO_OPTIONS = [
    { label: 'Todos', value: '' },
    { label: 'Ativo', value: 'S' },
    { label: 'Inativo', value: 'N' }
];

const TIPO_ESTABELECIMENTO_OPTIONS = [
    { label: 'Único', value: 'U' },
    { label: 'Matriz', value: 'M' },
    { label: 'Filial sem Capital Social', value: 'S' },
    { label: 'Filial com Capital Social', value: 'C' },
    { label: 'Filial com matriz em outra UF', value: 'O' },
    { label: 'Todos', value: '' }
];

const TIPO_GESTAO_OPTIONS = [
    { label: 'Todas', value: null },
    { label: 'Pública', value: ETipoGestaoPrestador.PUBLICA },
    { label: 'Privada', value: ETipoGestaoPrestador.PRIVADA },
    { label: 'Terceirizada', value: ETipoGestaoPrestador.TERCEIRIZADA }
];

interface MultiSelectOption {
    label: string;
    value: number;
}

function emptyFiltros(): RelatorioSelecaoPjPesquisaFiltros {
    return {
        nuCrm: '',
        sgUf: '',
        nuCgcPj: '',
        regCad: '',
        idSituacaoRegistro: '',
        idTipoInscricao: null,
        idSituacao: null,
        dtSituacaoInicio: null,
        dtSituacaoFim: null,
        nmPrestador: '',
        nmFantasia: '',
        idEspecialidade: '',
        idAreaAtuacao: '',
        idsRecursoHumano: [],
        contemTodosRh: false,
        idsRecursoMaterial: [],
        contemTodosRecursoMaterial: false,
        idsServico: [],
        contemTodosServico: false,
        idsAtividade: [],
        contemTodosAtividade: false,
        emitirApenasComCertificadoVencido: false,
        idSituacaoHistorico: null,
        dtHistoricoInicio: null,
        dtHistoricoFim: null,
        tipoEstabelecimento: '',
        idClassificacaoOpcao: null,
        tipoGestao: null,
        dtInscricaoInicio: null,
        dtInscricaoFim: null,
        idMunicipio: null,
        idOrgaoDelegacia: null,
        sgUfDelegacia: ''
    };
}

/** Severidade do selo de situação, na escala de `_tabela-tons.scss` (mesmo padrão do Acompanhamento de comissões). */
type SituacaoBadgeSeveridade = 'success' | 'danger' | 'secondary';

/** Severidade do selo de situação — mesma escala de tons do Acompanhamento de comissões (`_tabela-tons.scss`). */
function situacaoSeveridade(situacao: string): SituacaoBadgeSeveridade {
    const s = (situacao ?? '').toLowerCase();
    if (s.includes('ativo') || s.includes('regular')) return 'success';
    if (s.includes('cancel') || s.includes('inadimpl') || s.includes('suspen')) return 'danger';
    return 'secondary';
}

function toOptions(itens: PrestadorHistoricoOpcao[]): MultiSelectOption[] {
    return itens.map((i) => ({ label: i.descricao, value: i.id }));
}

/**
 * Modal de busca e seleção de prestadores para compor a fila de análise do relatório.
 * Espelha as 9 abas de filtro do legado SIEM-CNP (`UntRelatorioSelecao.pas`): Prestador,
 * Especialidade, R.H., Recursos Materiais, Serviços, Atividades, Histórico, Matriz/Filial e
 * Outros Filtros (Classificação, Tipo de Gestão, Inscrição e Município/Delegacia).
 */
@Component({
    selector: 'app-selecionar-prestadores',
    standalone: true,
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [
        DialogoPadraoDirective,
        FormsModule,
        TitleCasePipe,
        Button,
        CheckboxModule,
        DialogModule,
        SelectModule,
        MultiSelectModule,
        TableModule,
        DatePicker,
        ToggleSwitch,
        RadioButtonModule,
        InputTextModule,
        Tabs,
        TabList,
        Tab,
        TabPanels,
        TabPanel,
        PesquisaMunicipio,
        PesquisaDelegacia
    ],
    templateUrl: './selecionar-prestadores.html',
    styleUrl: './selecionar-prestadores.scss'
})
export class SelecionarPrestadores {
    private readonly relatorioService = inject(RelatorioSelecaoPjService);
    private readonly historicoService = inject(PrestadorHistoricoService);
    private readonly recursosHumanosService = inject(PrestadorRecursosHumanosService);
    private readonly recursosMateriaisService = inject(PrestadorRecursosMateriaisService);
    private readonly servicosService = inject(PrestadorServicosEspecialidadesService);
    private readonly atividadesService = inject(PrestadorAtividadesService);
    private readonly especialidadeService = inject(EspecialidadeService);
    private readonly messageService = inject(MessageService);
    private readonly destroyRef = inject(DestroyRef);

    /** Controla a visibilidade do dialog; suporta two-way binding via `[(visible)]`. */
    readonly visible = model<boolean>(false);

    /** Prestadores já presentes na fila de análise; usados para pré-marcar a seleção ao abrir. */
    readonly prestadoresSelecionados = input<PrestadorListItem[]>([]);

    /** Emite a lista completa de prestadores selecionados quando o usuário confirma. */
    readonly confirmados = output<PrestadorListItem[]>();

    readonly UF_SELECT_OPTIONS = UF_SELECT_OPTIONS;
    readonly REG_CAD_OPTIONS = REG_CAD_OPTIONS;
    readonly SITUACAO_REGISTRO_OPTIONS = SITUACAO_REGISTRO_OPTIONS;
    readonly TIPO_ESTABELECIMENTO_OPTIONS = TIPO_ESTABELECIMENTO_OPTIONS;
    readonly TIPO_GESTAO_OPTIONS = TIPO_GESTAO_OPTIONS;
    readonly situacaoSeveridade = situacaoSeveridade;

    // ── Filtros ───────────────────────────────────────────────────────────────
    filters: RelatorioSelecaoPjPesquisaFiltros = emptyFiltros();
    readonly filtersOpen = signal(false);
    readonly searchLoading = signal(false);
    readonly prestadoresList = signal<PrestadorListItem[]>([]);
    readonly searched = signal(false);

    // ── Lookups dinâmicos ─────────────────────────────────────────────────────
    readonly tiposInscricaoOptions = signal<MultiSelectOption[]>([]);
    readonly situacaoOptions = signal<MultiSelectOption[]>([]);
    readonly especialidades = signal<EspecialidadeListaResponse[]>([]);
    readonly areasAtuacao = signal<AreaAtuacaoPorEspecialidadeResponse[]>([]);
    readonly recursosHumanosOptions = signal<MultiSelectOption[]>([]);
    readonly recursosMateriaisOptions = signal<MultiSelectOption[]>([]);
    readonly servicosOptions = signal<MultiSelectOption[]>([]);
    readonly atividadesOptions = signal<MultiSelectOption[]>([]);
    readonly classificacaoOptions = signal<MultiSelectOption[]>([]);
    /** Situação (Histórico) depende deste tipo de inscrição — não é enviado ao backend (só filtra o combo). */
    readonly idTipoInscricaoHistorico = signal<number | null>(null);
    readonly situacaoHistoricoOptions = signal<MultiSelectOption[]>([]);

    // ── Aba 9 — Município/Delegacia (abrangência) ─────────────────────────────
    /** 'municipio' ou 'delegacia' — mutuamente exclusivos, como RdgLocalAbrangencia do legado. */
    readonly localAbrangencia = signal<'municipio' | 'delegacia'>('municipio');
    readonly municipioLabel = signal('');
    readonly delegaciaLabel = signal('');
    readonly municipioDialogVisible = signal(false);
    readonly delegaciaDialogVisible = signal(false);

    readonly especialidadeOptions = computed(() => [{ label: 'Todas', value: '' }, ...this.especialidades().map((e) => ({ label: e.dsEspecialidade ?? '', value: String(e.idEspecialidade ?? '') }))]);

    readonly areaAtuacaoOptions = computed(() => [{ label: 'Todas', value: '' }, ...this.areasAtuacao().map((a) => ({ label: a.dsAreaAtuacao ?? '', value: String(a.idAreaAtuacao ?? '') }))]);

    // ── Seleção interna do modal ──────────────────────────────────────────────
    readonly selectedInModal = signal<PrestadorListItem[]>([]);
    readonly totalSelecionados = computed(() => this.selectedInModal().length);

    constructor() {
        this.historicoService
            .listarTiposInscricao()
            .pipe(
                catchError(() => of<PrestadorHistoricoOpcao[]>([])),
                takeUntilDestroyed()
            )
            .subscribe((itens) => this.tiposInscricaoOptions.set(toOptions(itens)));

        this.especialidadeService
            .listar()
            .pipe(
                catchError(() => of<EspecialidadeListaResponse[]>([])),
                takeUntilDestroyed()
            )
            .subscribe((esps) => this.especialidades.set(esps));

        this.recursosHumanosService
            .tipos()
            .pipe(
                catchError(() => of([])),
                takeUntilDestroyed()
            )
            .subscribe((itens) => this.recursosHumanosOptions.set(itens.map((i) => ({ label: i.recursoHumano, value: i.idRecursoHumano }))));

        this.recursosMateriaisService
            .tipos()
            .pipe(
                catchError(() => of([])),
                takeUntilDestroyed()
            )
            .subscribe((itens) => this.recursosMateriaisOptions.set(itens.map((i) => ({ label: i.recursoMaterial, value: i.idRecursoMaterial }))));

        this.servicosService
            .listarServicos()
            .pipe(
                catchError(() => of([])),
                takeUntilDestroyed()
            )
            .subscribe((itens) => this.servicosOptions.set(itens.map((i) => ({ label: i.servico, value: i.idServico }))));

        this.atividadesService
            .opcoes()
            .pipe(
                catchError(() => of([])),
                takeUntilDestroyed()
            )
            .subscribe((itens) => this.atividadesOptions.set(itens.map((i) => ({ label: i.descricao, value: i.idAtividade }))));

        this.relatorioService
            .listarClassificacoes()
            .pipe(
                catchError(() => of([])),
                takeUntilDestroyed()
            )
            .subscribe((itens) => this.classificacaoOptions.set(itens.map((i) => ({ label: i.descricao, value: i.id }))));

        // Ao abrir o modal, sincroniza a seleção interna com a fila atual do pai.
        effect(() => {
            if (this.visible()) {
                untracked(() => this.selectedInModal.set([...this.prestadoresSelecionados()]));
            }
        });
    }

    // ── Filtros ───────────────────────────────────────────────────────────────
    toggleFilters(): void {
        this.filtersOpen.update((v) => !v);
    }

    /** Situação depende do Tipo de Inscrição selecionado — recarrega o catálogo dependente. */
    onTipoInscricaoChange(): void {
        this.filters.idSituacao = null;
        this.situacaoOptions.set([]);
        const idTipoInscricao = this.filters.idTipoInscricao;
        if (!idTipoInscricao) return;
        this.historicoService
            .listarSituacoes(idTipoInscricao)
            .pipe(
                catchError(() => of<PrestadorHistoricoOpcao[]>([])),
                takeUntilDestroyed(this.destroyRef)
            )
            .subscribe((itens) => this.situacaoOptions.set(toOptions(itens)));
    }

    onEspecialidadeChange(): void {
        this.filters.idAreaAtuacao = '';
        this.areasAtuacao.set([]);
        const id = this.filters.idEspecialidade;
        if (!id) return;
        this.especialidadeService
            .listarAreasAtuacaoPorEspecialidade(Number(id))
            .pipe(
                catchError(() => of<AreaAtuacaoPorEspecialidadeResponse[]>([])),
                takeUntilDestroyed(this.destroyRef)
            )
            .subscribe((areas) => this.areasAtuacao.set(areas));
    }

    // ── Aba 7 — Histórico ────────────────────────────────────────────────────
    /** Situação (Histórico) depende do Tipo de Inscrição (Histórico) selecionado — combo local. */
    onTipoInscricaoHistoricoChange(): void {
        this.filters.idSituacaoHistorico = null;
        this.situacaoHistoricoOptions.set([]);
        const idTipoInscricao = this.idTipoInscricaoHistorico();
        if (!idTipoInscricao) return;
        this.historicoService
            .listarSituacoes(idTipoInscricao)
            .pipe(
                catchError(() => of<PrestadorHistoricoOpcao[]>([])),
                takeUntilDestroyed(this.destroyRef)
            )
            .subscribe((itens) => this.situacaoHistoricoOptions.set(toOptions(itens)));
    }

    // ── Aba 9 — Outros Filtros: Município/Delegacia ──────────────────────────
    /** Alterna o tipo de seleção (Município x Abrangência), limpando o lado inativo (regra do legado). */
    onLocalAbrangenciaChange(): void {
        this.limparMunicipio();
        this.limparDelegacia();
    }

    selecionarMunicipio(m: MunicipioResponse): void {
        this.filters.idMunicipio = m.id;
        this.municipioLabel.set(`${m.dsMunicipio} - ${m.uf}`);
    }

    limparMunicipio(): void {
        this.filters.idMunicipio = null;
        this.municipioLabel.set('');
    }

    selecionarDelegacia(d: DelegaciaResumoApiResponse): void {
        this.filters.idOrgaoDelegacia = d.id;
        this.filters.sgUfDelegacia = d.uf;
        this.delegaciaLabel.set(d.delegacia);
    }

    limparDelegacia(): void {
        this.filters.idOrgaoDelegacia = null;
        this.filters.sgUfDelegacia = '';
        this.delegaciaLabel.set('');
    }

    /** Executa a pesquisa enviando apenas os filtros preenchidos ao backend. */
    pesquisar(): void {
        this.searchLoading.set(true);
        this.searched.set(true);
        this.prestadoresList.set([]);

        this.relatorioService
            .pesquisar(this.filters)
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe({
                next: (list) => {
                    this.prestadoresList.set(list);
                    this.searchLoading.set(false);
                },
                error: () => {
                    this.searchLoading.set(false);
                    this.messageService.add({ severity: 'error', summary: 'Erro', detail: 'Não foi possível pesquisar prestadores.' });
                }
            });
    }

    /** Limpa todos os filtros e os resultados de busca; preserva a seleção atual. */
    limparFiltros(): void {
        this.filters = emptyFiltros();
        this.situacaoOptions.set([]);
        this.areasAtuacao.set([]);
        this.prestadoresList.set([]);
        this.searched.set(false);
        this.idTipoInscricaoHistorico.set(null);
        this.situacaoHistoricoOptions.set([]);
        this.localAbrangencia.set('municipio');
        this.municipioLabel.set('');
        this.delegaciaLabel.set('');
    }

    // ── Seleção ───────────────────────────────────────────────────────────────
    isSelecionado(p: PrestadorListItem): boolean {
        return this.selectedInModal().some((s) => s.nuCrm === p.nuCrm && s.sgUf === p.sgUf);
    }

    toggleSelecionado(p: PrestadorListItem): void {
        if (this.isSelecionado(p)) {
            this.selectedInModal.update((list) => list.filter((s) => !(s.nuCrm === p.nuCrm && s.sgUf === p.sgUf)));
        } else {
            this.selectedInModal.update((list) => [...list, p]);
        }
    }

    /** Adiciona todos os prestadores da pesquisa atual à seleção, sem duplicar. */
    selecionarTodos(): void {
        const toAdd = this.prestadoresList().filter((p) => !this.isSelecionado(p));
        if (toAdd.length) this.selectedInModal.update((list) => [...list, ...toAdd]);
    }

    desmarcarTodos(): void {
        this.selectedInModal.set([]);
    }

    // ── Ações do modal ────────────────────────────────────────────────────────
    /** Emite a seleção confirmada e fecha o modal. */
    confirmar(): void {
        this.confirmados.emit(this.selectedInModal());
        this.visible.set(false);
    }

    cancelar(): void {
        this.visible.set(false);
    }
}
